import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react'; // Added Check icon

interface SearchableSelectProps {
    value: string | string[];
    onChange: (value: string | string[]) => void;
    options: string[];
    placeholder: string;
    className?: string;
    multiple?: boolean;
    disabled?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
    value,
    onChange,
    options,
    placeholder,
    className = '',
    multiple = false,
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    // Filtrar opciones basadas en búsqueda
    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        return options.filter(opt =>
            opt.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [options, searchTerm]);

    // Cerrar al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleSelect = (option: string) => {
        if (multiple) {
            const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
            const newValue = currentValues.includes(option)
                ? currentValues.filter(v => v !== option)
                : [...currentValues, option];
            onChange(newValue);
            // Don't close on multiple select
        } else {
            onChange(option);
            setIsOpen(false);
            setSearchTerm('');
        }
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(multiple ? [] : '');
        if (!multiple) setIsOpen(false);
        setSearchTerm('');
    };

    const isSelected = (option: string) => {
        if (multiple) {
            return Array.isArray(value) && value.includes(option);
        }
        return value === option;
    };

    const getDisplayText = () => {
        if (multiple) {
            const arr = Array.isArray(value) ? value : [];
            if (arr.length === 0) return '';
            if (arr.length === 1) return arr[0];
            return `${arr.length} seleccionados`; // O join(', ')
        }
        return value as string;
    };

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            {/* Trigger Button */}
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`
                    flex items-center justify-between w-full h-full
                    bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 hover:border-zinc-700
                    text-white text-xs uppercase font-bold 
                    py-2 px-3 rounded-sm outline-none 
                    focus:border-white focus:ring-1 focus:ring-white/20 transition-all duration-200
                    ${isOpen ? 'border-white bg-zinc-800 ring-1 ring-white/20' : ''}
                    ${disabled ? 'opacity-50 cursor-not-allowed hover:bg-zinc-900 hover:border-zinc-800' : ''}
                `}
            >
                <span className={`block truncate ${(!value || (Array.isArray(value) && value.length === 0)) ? 'text-zinc-500' : ''}`}>
                    {getDisplayText() || placeholder}
                </span>

                <div className="flex items-center ml-2">
                    {((!multiple && value) || (multiple && Array.isArray(value) && value.length > 0)) && (
                        <span
                            role="button"
                            onClick={handleClear}
                            className="p-1 hover:bg-zinc-800 rounded-full mr-1 text-zinc-500 hover:text-white transition-colors"
                        >
                            <X size={12} />
                        </span>
                    )}
                    <ChevronDown size={14} className={`text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute top-full left-0 mt-1 w-full min-w-[200px] bg-zinc-900 border border-zinc-700 rounded-sm shadow-xl z-50 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-2 border-b border-zinc-800 bg-zinc-900/95 sticky top-0 backdrop-blur-sm">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 w-3 h-3" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Buscar..."
                                autoFocus
                                className="w-full bg-zinc-800 text-white text-xs py-1.5 pl-7 pr-2 rounded-sm outline-none border border-transparent focus:border-white placeholder:text-zinc-600 uppercase font-bold"
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>

                    {/* Options List */}
                    <div className="max-h-60 overflow-y-auto overflow-x-hidden custom-scrollbar">
                        {filteredOptions.length === 0 ? (
                            <div className="p-3 text-center text-zinc-500 text-xs italic">
                                No hay resultados
                            </div>
                        ) : (
                            filteredOptions.map((option) => {
                                const active = isSelected(option);
                                return (
                                    <button
                                        key={option}
                                        type="button"
                                        onClick={() => handleSelect(option)}
                                        className={`
                                            w-full text-left px-4 py-2 text-xs uppercase font-bold
                                            hover:bg-zinc-800 hover:text-white transition-colors
                                            flex items-center justify-between
                                            ${active ? 'text-white bg-zinc-800/50' : 'text-zinc-400'}
                                        `}
                                        title={option}
                                    >
                                        <span className="truncate">{option}</span>
                                        {active && <Check size={12} className="text-white ml-2 shrink-0" />}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
